import axios, { AxiosRequestConfig, AxiosRequestHeaders, AxiosResponseHeaders, Method } from 'axios';
import { isDevelopmentEnv, isProductionEnv } from '../app/utils/utils';

export interface IRequest<T = any> {
    url: string;
    baseURL: string;
    data?: T;
}

export interface IResponse<D = any> {
    data: D;
    status: number;
    statusText: string;
    headers: TResponseHeaders;
    config: IRequestConfig;
    request?: any;
}

export type TRequestHeaders = AxiosRequestHeaders;

interface IRequestConfig extends AxiosRequestConfig { }

type TResponseHeaders = AxiosResponseHeaders;

type TMethod = Method;

const httpClient = () => {
    const _instance = axios.create({
        timeout: parseInt(process.env.TIMEOUT_HTTPCLIENT as string),
        withCredentials: isProductionEnv() || isDevelopmentEnv(),
    });

    const _createHeaders = (headers?: TRequestHeaders) => {
        return {
            [process.env.CONTENT_TYPE as string]: headers?.contentType || 'application/json',
            ...headers,
        };
    };

    const _createRequest = <T = any>(method: TMethod, request: IRequest<T>, headers?: TRequestHeaders): AxiosRequestConfig<T> => {
        return {
            method: method,
            baseURL: request.baseURL,
            url: request.url,
            data: request.data,
            headers: _createHeaders(headers),
        };
    };

    const clearCookies = () => {
        document.cookie = ``;
    }

    let isRetry = false;

    _instance.interceptors.response.use(
        res => res,
        async error => {
            if ((error.response.status === 401 || error.response.status === 403)) {
                if (!isRetry) {
                    try {
                        isRetry = true;
                        return _instance.request(error.config);
                    }
                    catch (error) {
                        console.log(error)
                        return (window.location.href = process.env.REACT_APP_EDESIGN_PORTAL_URL as any);
                    }
                } else {
                    console.log("clear cookies");
                    clearCookies();
                    //return (window.location.reload() as any);
                }
            }
            // Any status codes that falls outside the range of 2xx cause this function to trigger
            // Do something with response error
            // You can also log the error to an error reporting service
            return Promise.reject(error);
        }
    );

    const getAsync = async <D = any>(request: IRequest, headers?: TRequestHeaders): Promise<IResponse<D>> => {
        return _instance.request(_createRequest('get', request, headers));
    };

    const postAsync = async <T = any, D = any>(request: IRequest<T>, headers?: TRequestHeaders): Promise<IResponse<D>> => {
        return _instance.request(_createRequest('post', request, headers));
    };

    const putAsync = async <T = any, D = any>(request: IRequest<T>, headers?: TRequestHeaders): Promise<IResponse<D>> => {
        return _instance.request(_createRequest('put', request, headers));
    };

    const deleteAsync = async <D = any>(request: IRequest, headers?: TRequestHeaders): Promise<IResponse<D>> => {
        return _instance.request(_createRequest('delete', request, headers));
    };

    return {
        getAsync,
        postAsync,
        putAsync,
        deleteAsync,
    };
};

export default httpClient();
